/**
 * Template Settings Redux Slice
 * State management for template creation, editing, and management
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

// Async thunks

/**
 * Fetch predefined templates merged with their current status for a WABA
 */
export const fetchPredefinedStatus = createAsyncThunk(
  'templateSettings/fetchPredefinedStatus',
  async (wabaId, { rejectWithValue }) => {
    try {
      const response = await apiService.templateSettings.getPredefinedStatus();
      return response.data.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Apply a predefined template: create record + submit to Meta for review
 */
export const applyTemplate = createAsyncThunk(
  'templateSettings/apply',
  async ({ templateId, wabaId }, { rejectWithValue }) => {
    try {
      const response = await apiService.templateSettings.apply({ templateId, wabaId });
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Refresh a template's approval status directly from Meta
 */
export const refreshTemplateStatus = createAsyncThunk(
  'templateSettings/refreshStatus',
  async (templateId, { rejectWithValue }) => {
    try {
      const response = await apiService.templateSettings.refreshStatus(templateId);
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch all available default templates
 */
export const fetchAvailableTemplates = createAsyncThunk(
  'templateSettings/fetchAvailable',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiService.templateSettings.getAvailableDefaults();
      return response.data.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch specific default template by ID
 */
export const fetchDefaultTemplate = createAsyncThunk(
  'templateSettings/fetchDefault',
  async (templateId, { rejectWithValue }) => {
    try {
      const response = await apiService.templateSettings.getDefault(templateId);
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Create new template from default
 */
export const createTemplate = createAsyncThunk(
  'templateSettings/create',
  async ({ templateId, wabaId, customData }, { rejectWithValue }) => {
    try {
      const response = await apiService.templateSettings.create({
        templateId,
        wabaId,
        customData
      });
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch all user's templates
 */
export const fetchUserTemplates = createAsyncThunk(
  'templateSettings/fetchUserTemplates',
  async ({ wabaId, templateType, page = 1, limit = 20 }, { rejectWithValue }) => {
    try {
      const response = await apiService.templateSettings.getAll({
        wabaId,
        templateType,
        page,
        limit
      });
      return {
        items: response.data.data || [],
        pagination: response.data.pagination || { page, limit, total: 0 }
      };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Fetch single template
 */
export const fetchTemplate = createAsyncThunk(
  'templateSettings/fetchTemplate',
  async (templateSettingsId, { rejectWithValue }) => {
    try {
      const response = await apiService.templateSettings.getById(templateSettingsId);
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Update template
 */
export const updateTemplate = createAsyncThunk(
  'templateSettings/update',
  async ({ templateSettingsId, updates }, { rejectWithValue }) => {
    try {
      const response = await apiService.templateSettings.update(
        templateSettingsId,
        updates
      );
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Delete template
 */
export const deleteTemplate = createAsyncThunk(
  'templateSettings/delete',
  async (templateSettingsId, { rejectWithValue }) => {
    try {
      await apiService.templateSettings.delete(templateSettingsId);
      return templateSettingsId;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Record template usage
 */
export const recordTemplateUsage = createAsyncThunk(
  'templateSettings/recordUsage',
  async (templateSettingsId, { rejectWithValue }) => {
    try {
      const response = await apiService.templateSettings.recordUsage(templateSettingsId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Send invoice template message
 */
export const sendInvoiceTemplate = createAsyncThunk(
  'templateSettings/sendInvoice',
  async ({ templateSettingsId, phoneNumberId, recipientPhone, invoiceData }, { rejectWithValue }) => {
    try {
      const response = await apiService.templateSettings.sendInvoice({
        templateSettingsId,
        phoneNumberId,
        recipientPhone,
        invoiceData
      });
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/**
 * Send flow template message
 */
export const sendFlowTemplate = createAsyncThunk(
  'templateSettings/sendFlow',
  async ({ templateSettingsId, phoneNumberId, recipientPhone, flowData }, { rejectWithValue }) => {
    try {
      const response = await apiService.templateSettings.sendFlow({
        templateSettingsId,
        phoneNumberId,
        recipientPhone,
        flowData
      });
      return response.data.data || response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial state
const initialState = {
  // Available templates (defaults)
  availableTemplates: [],
  
  // Predefined templates merged with their current status per WABA
  predefinedTemplates: [],

  // Selected default template for preview/creation
  selectedDefaultTemplate: null,
  
  // User's created templates
  items: [],
  selectedItem: null,
  
  // UI state
  loading: false,
  applyingTemplateId: null, // templateId currently being applied
  error: null,
  
  // Pagination
  pagination: {
    page: 1,
    limit: 20,
    total: 0
  },
  
  // Modal state
  isCreateModalOpen: false,
  isEditModalOpen: false,
  
  // Creation/edit form state
  formData: {
    templateId: null,
    wabaId: null,
    customData: {}
  }
};

// Slice
const templateSettingsSlice = createSlice({
  name: 'templateSettings',
  initialState,
  reducers: {
    // Synchronous actions
    clearError: (state) => {
      state.error = null;
    },
    
    setSelectedDefaultTemplate: (state, action) => {
      state.selectedDefaultTemplate = action.payload;
    },
    
    setSelectedItem: (state, action) => {
      state.selectedItem = action.payload;
    },
    
    openCreateModal: (state) => {
      state.isCreateModalOpen = true;
      state.formData = {
        templateId: null,
        wabaId: null,
        customData: {}
      };
    },
    
    closeCreateModal: (state) => {
      state.isCreateModalOpen = false;
      state.formData = {
        templateId: null,
        wabaId: null,
        customData: {}
      };
    },
    
    openEditModal: (state, action) => {
      state.isEditModalOpen = true;
      state.selectedItem = action.payload;
    },
    
    closeEditModal: (state) => {
      state.isEditModalOpen = false;
      state.selectedItem = null;
    },
    
    updateFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    
    resetFormData: (state) => {
      state.formData = {
        templateId: null,
        wabaId: null,
        customData: {}
      };
    },
    
    setCurrentPage: (state, action) => {
      state.pagination.page = action.payload;
    }
  },
  
  extraReducers: (builder) => {
    // Fetch available templates
    builder
      .addCase(fetchAvailableTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAvailableTemplates.fulfilled, (state, action) => {
        state.availableTemplates = action.payload;
        state.loading = false;
      })
      .addCase(fetchAvailableTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    
    // Fetch default template
    builder
      .addCase(fetchDefaultTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDefaultTemplate.fulfilled, (state, action) => {
        state.selectedDefaultTemplate = action.payload;
        state.loading = false;
      })
      .addCase(fetchDefaultTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    
    // Create template
    builder
      .addCase(createTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.items.unshift(action.payload);
        state.loading = false;
        state.isCreateModalOpen = false;
        state.formData = {
          templateId: null,
          wabaId: null,
          customData: {}
        };
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    
    // Fetch user templates
    builder
      .addCase(fetchUserTemplates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserTemplates.fulfilled, (state, action) => {
        state.items = action.payload.items;
        state.pagination = action.payload.pagination;
        state.loading = false;
      })
      .addCase(fetchUserTemplates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    
    // Fetch single template
    builder
      .addCase(fetchTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTemplate.fulfilled, (state, action) => {
        state.selectedItem = action.payload;
        state.loading = false;
      })
      .addCase(fetchTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    
    // Update template
    builder
      .addCase(updateTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTemplate.fulfilled, (state, action) => {
        const updated = action.payload;
        const index = state.items.findIndex(item => item._id === updated._id);
        if (index !== -1) {
          state.items[index] = updated;
        }
        // Sync predefined templates list
        const pidx = state.predefinedTemplates.findIndex(t => t.id === updated.templateId);
        if (pidx !== -1) {
          state.predefinedTemplates[pidx] = {
            ...state.predefinedTemplates[pidx],
            record: updated,
            status: updated.status,
            recordId: updated._id
          };
        }
        state.selectedItem = updated;
        state.loading = false;
        state.isEditModalOpen = false;
      })
      .addCase(updateTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    
    // Delete template
    builder
      .addCase(deleteTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTemplate.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item._id !== action.payload);
        state.loading = false;
      })
      .addCase(deleteTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
    
    // Record usage
    builder
      .addCase(recordTemplateUsage.pending, (state) => {
        state.error = null;
      })
      .addCase(recordTemplateUsage.fulfilled, (state) => {
        // Usage recorded silently
      })
      .addCase(recordTemplateUsage.rejected, (state, action) => {
        state.error = action.payload;
      });

    // Send invoice template
    builder
      .addCase(sendInvoiceTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendInvoiceTemplate.fulfilled, (state, action) => {
        state.loading = false;
        // Template is prepared, actual sending happens in message controller
      })
      .addCase(sendInvoiceTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Send flow template
    builder
      .addCase(sendFlowTemplate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendFlowTemplate.fulfilled, (state, action) => {
        state.loading = false;
        // Template is prepared, actual sending happens in message controller
      })
      .addCase(sendFlowTemplate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Fetch predefined status
    builder
      .addCase(fetchPredefinedStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPredefinedStatus.fulfilled, (state, action) => {
        state.predefinedTemplates = action.payload;
        state.loading = false;
      })
      .addCase(fetchPredefinedStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // Apply template
    builder
      .addCase(applyTemplate.pending, (state, action) => {
        state.applyingTemplateId = action.meta.arg.templateId;
        state.error = null;
      })
      .addCase(applyTemplate.fulfilled, (state, action) => {
        state.applyingTemplateId = null;
        // Update the matching predefined template entry with the new status
        const updated = action.payload;
        const idx = state.predefinedTemplates.findIndex(t => t.id === updated.templateId);
        if (idx !== -1) {
          state.predefinedTemplates[idx] = {
            ...state.predefinedTemplates[idx],
            record: updated,
            status: updated.status,
            recordId: updated._id,
            metaTemplateId: updated.metaTemplateId,
            metaSubmitError: updated.metaSubmitError
          };
        }
      })
      .addCase(applyTemplate.rejected, (state, action) => {
        state.applyingTemplateId = null;
        state.error = action.payload;
      });

    // Refresh template status from Meta
    builder
      .addCase(refreshTemplateStatus.pending, (state, action) => {
        state.applyingTemplateId = action.meta.arg;
        state.error = null;
      })
      .addCase(refreshTemplateStatus.fulfilled, (state, action) => {
        state.applyingTemplateId = null;
        const updated = action.payload;
        // Arg is the metaTemplateId (route param), not the internal templateId
        const idx = state.predefinedTemplates.findIndex(
          t => t.metaTemplateId === action.meta.arg || t.id === updated.templateId
        );
        if (idx !== -1) {
          state.predefinedTemplates[idx] = {
            ...state.predefinedTemplates[idx],
            record: updated,
            status: updated.status,
            recordId: updated._id,
            metaTemplateId: updated.metaTemplateId,
            metaSubmitError: updated.metaSubmitError
          };
        }
      })
      .addCase(refreshTemplateStatus.rejected, (state, action) => {
        state.applyingTemplateId = null;
        state.error = action.payload;
      });

  }
});

// Actions
export const {
  clearError,
  setSelectedDefaultTemplate,
  setSelectedItem,
  openCreateModal,
  closeCreateModal,
  openEditModal,
  closeEditModal,
  updateFormData,
  resetFormData,
  setCurrentPage
} = templateSettingsSlice.actions;

export default templateSettingsSlice.reducer;
